import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellRing, Check, CheckCircle, Package, MessageSquare,
  Truck, AlertTriangle, Target, ClipboardList, ChevronRight,
  ChevronDown, Settings, X, Clock, Shield, Megaphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useNotificationHub, GroupedNotification } from '@/hooks/useNotificationHub';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { resolveNotificationUrl, getNotificationActions } from '@/lib/notificationDeepLinks';
import { localizeNotification } from '@/lib/notificationI18n';

const CATEGORY_CONFIG: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  booking: { icon: ClipboardList, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  offer: { icon: Target, color: 'text-red-400', bg: 'bg-red-500/15' },
  message: { icon: MessageSquare, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
  trip: { icon: Truck, color: 'text-orange-400', bg: 'bg-orange-500/15' },
  system: { icon: Shield, color: 'text-purple-400', bg: 'bg-purple-500/15' },
  general: { icon: Bell, color: 'text-blue-400', bg: 'bg-blue-500/15' },
  payment: { icon: Package, color: 'text-green-400', bg: 'bg-green-500/15' },
  dispute: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/15' },
};

const TYPE_ICONS: Record<string, typeof Bell> = {
  new_bid: Target, match_offer_new: Target, match_offer_accepted: CheckCircle,
  booking_created: ClipboardList, booking_confirmed: CheckCircle, booking_cancelled: X, booking_completed: CheckCircle,
  question_received: MessageSquare, answer_received: MessageSquare, new_message: MessageSquare,
  trip_created: Truck, trip_updated: Truck, trip_completed: CheckCircle,
  system_announcement: Megaphone, delivery_pickup: Package, delivery_transit: Truck, delivery_complete: CheckCircle,
};

function formatTime(dateString: string): string {
  try { return formatDistanceToNow(new Date(dateString), { addSuffix: true }); } catch { return 'just now'; }
}

function getPriorityIndicator(priority: string) {
  if (priority === 'urgent') return 'border-s-4 border-s-red-500';
  if (priority === 'high') return 'border-s-4 border-s-orange-400';
  return '';
}

function NotificationCard({ group, onAction, onMarkRead, t, role }: { group: GroupedNotification; onAction: (url: string) => void; onMarkRead: () => void; t: (key: string, opts?: any) => string; role?: string }) {
  const [expanded, setExpanded] = useState(false);
  const config = CATEGORY_CONFIG[group.category] || CATEGORY_CONFIG.general;
  const IconComponent = TYPE_ICONS[group.notifications[0]?.type] || config.icon;

  const deepUrl = group.notifications[0] ? resolveNotificationUrl(group.notifications[0], role) : group.action_url;

  // Localize display: for single notifications, translate via type keys.
  // For grouped, translate the scaffold and localize the latest child.
  const latest = group.notifications[0];
  const latestLocalized = latest ? localizeNotification(latest as any, t as any) : null;
  let displayTitle = group.title;
  let displayMessage = group.message;
  if (latestLocalized) {
    if (!group.isGrouped) {
      displayTitle = latestLocalized.title || group.title;
      displayMessage = latestLocalized.message || group.message;
    } else {
      const count = group.notifications.length;
      const tp = latest.type || '';
      if (tp === 'question_received' || tp === 'answer_received' || tp === 'new_message') {
        const senderName = (latest as any).data?.courier_name || (latest as any).data?.customer_name || t('notif.grouped.someone', 'Someone');
        displayTitle = t('notif.grouped.messagesFrom', { defaultValue: '{{name}} sent {{count}} messages', name: senderName, count });
        displayMessage = latestLocalized.message || group.message;
      } else if (tp === 'new_bid' || tp === 'match_offer_new') {
        displayTitle = t('notif.grouped.newOffers', { defaultValue: '{{count}} new courier offers received', count });
        displayMessage = t('notif.grouped.latestPrefix', { defaultValue: 'Latest: {{msg}}', msg: latestLocalized.message || '' });
      } else if (tp?.startsWith('shipment_') || tp?.startsWith('delivery_')) {
        displayTitle = t('notif.grouped.shipmentUpdate', 'Shipment update');
        displayMessage = t('notif.grouped.updatesLatest', { defaultValue: '{{count}} updates – Latest: {{msg}}', count, msg: latestLocalized.message || '' });
      } else {
        displayTitle = `${count} ${latestLocalized.title || group.title}`;
        displayMessage = latestLocalized.message || group.message;
      }
    }
  }

  const handleClick = () => {
    if (group.isGrouped) setExpanded(!expanded);
    else if (deepUrl) onAction(deepUrl);
    if (group.hasUnread) onMarkRead();
  };

  const quickActions = group.notifications[0] ? getNotificationActions(group.notifications[0], role, t) : [];

  return (
    <div className={cn('transition-all duration-150', getPriorityIndicator(group.priority), group.hasUnread ? 'bg-white/[0.03]' : 'bg-transparent', 'hover:bg-white/[0.05] cursor-pointer')}>
      <div className="flex items-start gap-3 px-3 sm:px-4 py-3.5" onClick={handleClick}>
        <div className={cn('flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center mt-0.5', config.bg)}>
          <IconComponent className={cn('h-[14px] w-[14px]', config.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className={cn('text-[13px] leading-snug break-words', group.hasUnread ? 'font-semibold text-[var(--portal-text)]' : 'font-medium text-[var(--portal-text-secondary)]')}>{displayTitle}</p>
              <p className="text-[11px] text-[var(--portal-text-secondary)] mt-1 line-clamp-2 leading-relaxed break-words">{displayMessage}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {group.hasUnread && <div className="w-2 h-2 rounded-full bg-[var(--color-orange)]" />}
              {group.isGrouped && <Badge variant="secondary" className="text-[10px] h-5 min-w-[20px] px-1 font-bold rounded-md bg-white/[0.08] text-[var(--portal-text)] border-0">{group.notifications.length}</Badge>}
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-2">
            <span className="text-[10px] text-[var(--portal-text-secondary)]/70 flex items-center gap-1"><Clock className="h-3 w-3 opacity-60" />{formatTime(group.latestAt)}</span>
            {group.priority === 'urgent' && <Badge variant="destructive" className="text-[9px] h-[18px] px-1.5 rounded-md bg-red-500/15 text-red-400 border-0">{t("portal.notifications.urgent")}</Badge>}
            {group.priority === 'high' && <Badge className="text-[9px] h-[18px] px-1.5 rounded-md bg-[var(--color-navy)] text-[var(--color-orange)] border-0" variant="outline">{t("portal.notifications.high")}</Badge>}
          </div>
          {quickActions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              {quickActions.map((action, i) => (
                <Button key={i} variant={action.primary ? 'default' : 'outline'} size="sm"
                  className={cn('h-7 text-[11px] px-2.5 rounded-md font-medium', action.primary ? 'bg-[var(--color-orange)]/15 hover:bg-[var(--color-orange)]/25 text-[var(--color-orange)] border border-[var(--color-orange)]/30' : 'bg-white/[0.06] border-white/[0.1] text-[var(--portal-text-secondary)] hover:bg-white/[0.1]')}
                  onClick={(e) => { e.stopPropagation(); onAction(action.url); }}>{action.label}</Button>
              ))}
            </div>
          )}
          {expanded && group.isGrouped && (
            <div className="mt-3 space-y-0.5 ps-2 border-s-2 border-white/[0.08] ms-0.5">
              {group.notifications.map((n) => {
                const loc = localizeNotification(n as any, t as any);
                return (
                <div key={n.id} className="py-2 px-3 text-[11px] text-[var(--portal-text-secondary)] hover:bg-white/[0.04]  cursor-pointer" onClick={(e) => { e.stopPropagation(); onAction(resolveNotificationUrl(n, role)); }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={cn('truncate', !n.is_read && 'font-medium text-[var(--portal-text)]')}>{loc.title || n.title}</span>
                    <span className="text-[9px] text-[var(--portal-text-secondary)]/60 shrink-0">{formatTime(n.created_at)}</span>
                  </div>
                  <p className="text-[var(--portal-text-secondary)]/70 mt-0.5 line-clamp-1">{loc.message || n.message}</p>
                </div>
                );
              })}
            </div>
          )}
        </div>
        {group.isGrouped && ( 
          <div className="flex-shrink-0 mt-1.5">{expanded ? <ChevronDown className="h-3.5 w-3.5 text-[var(--portal-text-secondary)]/60" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--portal-text-secondary)]/60 rtl:rotate-180" />}</div>
        )}
      </div>
    </div>
  );
}

export default function NotificationHub() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { notifications: grouped, unreadCount, isLoading, activeTab, setActiveTab, tabCounts, markGroupAsRead, markAllAsRead, isOpen, setIsOpen } = useNotificationHub();

  const userRole = user?.role === 'courier' ? 'courier' : 'customer';

  const handleAction = (url: string) => { setIsOpen(false); navigate(url); };

  const TAB_ITEMS = [
    { key: 'all' as const, label: t("portal.notifications.all") },
    { key: 'bookings' as const, label: t("portal.notifications.bookings") },
    { key: 'offers' as const, label: t("portal.notifications.offers") },
    { key: 'messages' as const, label: t("portal.notifications.messagesTab") },
    { key: 'system' as const, label: t("portal.notifications.system") },
  ];

  const bellButton = (
    <button onClick={() => setIsOpen(true)} className={cn('relative p-2 rounded-md hover:bg-white/10 transition-colors', unreadCount > 0 && 'animate-pulse-subtle')}
      aria-label={unreadCount > 0 ? `${unreadCount} notifications` : 'Notifications'}>
      {unreadCount > 0 ? <BellRing className="h-5 w-5 text-[var(--color-orange)]" /> : <Bell className="h-5 w-5 text-white/70" />}
      {unreadCount > 0 && <span className="absolute -top-0.5 -end-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
    </button>
  );

  const hubContent = (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 sm:px-4 pt-4 pb-3 border-b border-white/[0.08]">
        <div className="flex items-center justify-between mb-3.5 gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-md bg-[var(--color-orange)]/15 flex items-center justify-center shrink-0">
              <Bell className="h-4 w-4 text-[var(--color-orange)]" />
            </div>
            <h2 className="font-semibold text-[15px] text-[var(--portal-text)] truncate">{t("portal.notifications.notifications")}</h2>
            {unreadCount > 0 && <Badge className="bg-red-500 text-white text-[10px] h-5 min-w-[20px] px-1.5 rounded-md font-bold">{unreadCount}</Badge>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-[11px] h-7 px-2 text-[var(--color-orange)] hover:text-[var(--color-orange)] hover:bg-[var(--color-navy)] rounded-md">
                <Check className="h-3 w-3 me-1" />{t("portal.notifications.readAll")}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="h-7 w-7 p-0 text-[var(--portal-text-secondary)] hover:text-[var(--portal-text)] hover:bg-white/[0.08] rounded-md">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide touch-pan-x" data-allow-horizontal-scroll="true">
          {TAB_ITEMS.map((tab) => {
            const count = tabCounts[tab.key];
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={cn('flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium whitespace-nowrap transition-all rounded-md border shrink-0', activeTab === tab.key ? 'border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)] border-[var(--color-navy)] shadow-sm shadow-[var(--color-orange)]/20' : 'bg-white/[0.06] text-[var(--portal-text-secondary)] border-white/[0.08] hover:bg-white/[0.1] hover:text-[var(--portal-text)]')}>
                <span>{tab.label}</span>
                {count > 0 && <span className={cn('text-[10px] font-bold h-4 min-w-[16px] flex items-center justify-center px-1 rounded-full', activeTab === tab.key ? 'bg-white/25 text-white' : 'bg-red-500/90 text-white')}>{count}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-4 sm:p-6 space-y-3">{[...Array(4)].map((_, i) => (<div key={i} className="flex items-start gap-3"><div className="w-9 h-9 ec-skel" /><div className="flex-1 space-y-2"><div className="h-3 ec-skel w-3/4" /><div className="h-2.5 ec-skel w-1/2" /></div></div>))}</div>
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 md:py-16 px-6 text-center"> 
            <div className="w-14 h-14  bg-[var(--portal-badge-bg)] flex items-center justify-center mb-4"><Bell className="h-6 w-6 text-[var(--portal-text-secondary)]" /></div>
            <h3 className="text-sm font-semibold text-[var(--portal-text)] mb-1">{t("portal.notifications.allCaughtUp")}</h3>
            <p className="text-xs text-[var(--portal-text-secondary)] max-w-[200px]">
              {activeTab !== 'all' ? t("portal.notifications.noNotificationsYet", { tab: activeTab }) : t("portal.notifications.wellNotifyYou")}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {grouped.map((group) => (<NotificationCard key={group.key} group={group} onAction={handleAction} onMarkRead={() => markGroupAsRead(group)} t={t} role={userRole} />))}
          </div>
        )}
      </ScrollArea>
      {grouped.length > 0 && (
        <div className="border-t border-white/[0.08] p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs text-[var(--portal-text-secondary)] hover:text-[var(--color-orange)] hover:bg-[var(--color-orange)]/5 "
            onClick={() => { setIsOpen(false); navigate(user?.role === 'courier' ? '/courier/notifications' : '/account/notifications'); }}>
            {t("portal.notifications.viewAll")}<ChevronRight className="h-3 w-3 ms-1 rtl:rotate-180" /> 
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {bellButton}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side={isMobile ? 'bottom' : (isRTL ? 'left' : 'right')}
          data-portal="courier"
          style={{
            ['--portal-bg' as any]: '#0C1525',
            ['--portal-surface' as any]: '#132035',
            ['--portal-surface-alt' as any]: '#0F1A2E',
            ['--portal-surface-hover' as any]: 'rgba(255,255,255,0.04)',
            ['--portal-border' as any]: 'rgba(255,255,255,0.08)',
            ['--portal-text' as any]: '#F1F5F9',
            ['--portal-text-secondary' as any]: 'rgba(241,245,249,0.65)',
            ['--portal-badge-bg' as any]: 'rgba(255,255,255,0.06)',
          }}
          className={cn(
            'p-0 flex flex-col border-white/[0.08] [&>button.absolute]:hidden bg-[#132035] text-[#F1F5F9]',
            isMobile ? 'h-[85vh] ' : 'w-[400px] max-w-[90vw]'
          )}
        >
          <SheetHeader className="sr-only"><SheetTitle>{t("portal.notifications.notifications")}</SheetTitle></SheetHeader>
          {hubContent}
        </SheetContent>
      </Sheet>
    </>
  );
}
